import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { SltSyncErrorService } from '../../services/slt-sync-error.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-slt-sync-error',
    templateUrl: './slt-sync-error.component.html',
    styleUrls: ['./slt-sync-error.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltSyncErrorComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = 'Group Sync Error';
  minimise = false;
  loading = false;

  // Component-specific properties
  jsonView: string = '';

  // Global settings
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private sltSyncErrorService: SltSyncErrorService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Load sync error JSON data
        this.getLogJSON();
      }
    } catch (e) {
      console.error('Error in SltSyncError init:', e);
      this.loading = false;
    }
  }

  /**
   * Get sync error log JSON data
   */
  getLogJSON() {
    this.loading = true;
    this.cdr.detectChanges();

    this.sltSyncErrorService.getSltSyncData(
      this.GENERAL_CONFIG.base_url,
      'GROUP_SYNC_ERROR',
      this.rowData.LOG_ID
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == "1") {
          try {
            // Format JSON for display
            const responseData = response.jsonObject[0].RESPONSE;
            this.jsonView = this.formatJsonView(responseData);
            this.loading = false;
            this.cdr.detectChanges();
          } catch (parseError) {
            console.error('Error parsing response data:', parseError);
            this.loading = false;
            this._snackBar.open('Error parsing response data', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.cdr.detectChanges();
          }
        } else {
          this.loading = false;
          this._snackBar.open(response.message || 'Error loading sync error data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.loading = false;
        console.error('Error loading sync error data:', error);
        this._snackBar.open('Error loading sync error data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Format JSON object to string for display
   * @param obj JSON object or string to format
   * @returns Formatted JSON string
   */
  formatJsonView(obj: any): string {
    try {
      // If it's already a string, try to parse it
      if (typeof obj === 'string') {
        obj = safeJsonInput<any>(obj, null, 'slt-sync-error.parse1');
      }
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      // If parsing fails, return as is
      return obj;
    }
  }

  /**
   * Cancel and close modal
   */
  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
